import Vue from 'vue';
import { mapGetters } from 'vuex';
import _ from 'lodash';
import { guid } from '@/mixins/guid';
import { modals } from '@/mixins/modal';
import { uploader } from '@/mixins/aws/uploader/s3';
import linkPage from '@editor/droplets/_bubble-options/components/link-page';
import uploadProgress from '@/mixins/aws/uploader';
import path from 'path';

export default Vue.component('edit-carousel', {
  template: require('./template.html'),
  components: {
    linkPage,
    uploadProgress,
  },
  computed: {
    ...mapGetters(['viewport', 'currentPage', 'currentSite']),
    images() {
      return this.droplet.images;
    },
    minCount() {
      const total = this.droplet.images.length;
      let disableClass = false;
      if (total <= 2) {
        disableClass = true;
      }
      return disableClass;
    },
    maxCount() {
      const total = this.droplet.images.length;
      let disableClass = false;
      if (total >= 30) {
        disableClass = true;
      }
      return disableClass;
    },
  },
  data() {
    return {
      paginate: ['images'],
      showImgModal: false,
      activeItem: null,
      loading: false,
      prefix: 'drz_carousel_',
      bucketPath: `_droplet-assets/carousel/${this.droplet._id}/`,
      thumbnails: `_droplet-assets/_thumbnails/carousel/${this.droplet._id}/`,
      delayInput: null,
      activeImage: null,
      visible: this.droplet.visible,
      visibleOpts: [
        { number: '1', value: '1' },
        { number: '2', value: '2' },
        { number: '3', value: '3' },
        { number: '4', value: '4' },
        { number: '5', value: '5' },
        { number: '6', value: '6' },
      ],
    };
  },
  name: 'edit-carousel',
  mixins: [guid, modals, uploader],
  props: ['droplet', 'show-edit-bubble'],
  methods: {
    getThumbnail(image) {
      let thumb = this._getThumbFromFile(image.src);
      if (image.src.startsWith('/')) {
        thumb = `${this._servePrefix(image.src)}/${this.thumbnails}${thumb}`;
      }
      return `url('${thumb}')`;
    },
    openUpload() {
      this.$refs.uploadFile.click();
      // reset local rearranges on open
      this.$store.dispatch('setCarouselLimits', {
        drp: this.droplet,
        showAll: false,
        itemLimit: 5,
      });
    },
    async uploadImages(e) {
      this.$validator.validateAll().then(async (result) => {
        // run only if file types and dimensions are correct
        if (result) {
          this.loading = true;
          const newFiles = [...e.target.files];
          if (newFiles.length > 0) {
            if (!this.droplet.firstUpload) {
              const payload = {
                drp: this.droplet,
                value: true,
              };
              await this.$store.dispatch('setCarouselFirst', payload);
            }
            try {
              // check for expired sessions first
              await this._cognitoCheckSession();
              await Promise.all(
                newFiles.map(async (f) => {
                  let fileName = path.basename(f.name);
                  fileName = `${this.prefix}${_.trim(fileName)}`;
                  fileName = _.replace(fileName, /\s+/g, '_');
                  const filePath = `/${this.bucketPath}${fileName}`;
                  await this.uploadFile({
                    file: `${this._sitePath}${filePath}`,
                    body: f,
                    thumbnail: {
                      width: 35,
                      srcFile: f,
                      name: fileName,
                      bucketPath: `${this._sitePath}/${this.thumbnails}`,
                    },
                  });
                  // update image array
                  await this.$store.dispatch('addCarouselImage', {
                    drp: this.droplet,
                    newImage: {
                      _id: this.guid(),
                      src: filePath,
                      altText: path.basename(filePath),
                      href: false,
                      newWindow: false,
                      reference: false,
                    },
                  });
                }),
              );
            } catch (err) {
              if (!err.message.match(/aborted/gi)) {
                this.$log.error(err);
                await this._reportError({
                  error: err,
                  fn: 'uploadImages - carousel',
                });
              }
            } finally {
              this.loading = false;
              this.bouncePlugin();
            }
          }
          // reset value to null so we can get the "change" event to run
          // in any case. Even when the user selects the same file
          e.target.value = null;
        }
      });
    },
    async showAllImages(e) {
      const checked = e.currentTarget.checked;
      const payload = {};
      payload.drp = this.droplet;
      payload.showAll = checked;
      if (checked) {
        payload.itemLimit = this.droplet.images.length;
      } else {
        payload.itemLimit = 5;
      }
      await this.$store.dispatch('setCarouselLimits', payload);
      this.bouncePlugin();
    },
    getImg(image) {
      return path.basename(image.src);
    },
    findIndex(id) {
      // we need to find the index from original array since
      // the template is looping through the paginated array
      let i;
      _.forEach(this.droplet.images, (img, index) => {
        if (img._id === id) {
          i = index;
        }
      });
      return {
        index: i,
        template: i + 1,
      };
    },
    confirmDelete(image) {
      const total = this.droplet.images.length;
      if (total > 2) {
        this.activeItem = image;
        this.toggleModal();
      }
    },
    async removeImg(image) {
      const total = this.droplet.images.length;
      if (total > 2) {
        const payload = {};
        payload.drp = this.droplet;
        payload.imgIndex = this.findIndex(image._id);
        // remove image then bounce plugin
        await this.$store.dispatch('removeCarouselImg', payload);
        this.bouncePlugin();

        // send off to be deleted from s3 if not default images
        if (this.droplet.firstUpload) {
          this.loading = true;
          // prep notifcation type
          const notif = {
            id: this.guid(),
          };
          const file = path.basename(image.src);
          const thumb = this._getThumbFromFile(image.src);
          try {
            // delete original from s3
            await this._s3deleteFile({
              key: `${this._fullSiteFolder}/${this.bucketPath}${file}`,
            });
            // delete thumbnail from s3
            await this._s3deleteFile({
              key: `${this._fullSiteFolder}/${this.thumbnails}${thumb}`,
            });
            // set for notifications
            notif.type = 'success';
            notif.message = `Successfully deleted image: ${file}`;
          } catch (err) {
            this.$log.error('error: ', err);
            // set for notifications
            notif.type = 'error';
            notif.message = `${err.message || err}. This has been reported.`;
            await this._reportError({
              error: err,
              fn: 'removeImg',
            });
          } finally {
            // send off to notifications
            this.$store.dispatch('addNotification', notif);
            // kill loader
            this.loading = false;
            this.bouncePlugin();
          }
        }
      }
    },
    setAltText(e, image) {
      this._debounce(() => {
        const text = e.target.value;
        const val = text.trim() !== '' ? text : false;
        this.$store.dispatch('setDropletVal', {
          drp: image,
          key: 'altText',
          val,
        });
      }, 250);
    },
    getImgName(name) {
      return path.basename(name);
    },
    setDelay(e) {
      const val = e.currentTarget.value;
      this.delayInput = val;
      const payload = {};
      payload.drp = this.droplet;
      payload.delay = val;
      if (val > 0) {
        this.promise(this.$store.dispatch('setCarouselDelay', payload), 'setting delay');
      }
    },
    setVisibleNum(val) {
      const payload = {};
      payload.drp = this.droplet;
      payload.visible = val;
      this.promise(
        this.$store.dispatch('setCarouselVisibleNum', payload),
        'setting visible number',
      );
    },
    setControls(e) {
      const hidden = e.currentTarget.checked;
      const payload = {};
      payload.drp = this.droplet;
      if (hidden) {
        payload.controls = 'hide';
      } else {
        payload.controls = 'show';
      }
      this.promise(this.$store.dispatch('setCarouselControls', payload), 'setting controls');
    },
    bouncePlugin() {
      this.$vnode.context.unBindPlugin();
      this.$vnode.context.bindPlugin();
    },
    async drzRevertData() {
      // rebind plugin in case user removed items then cancelled.
      this.$emit('css-generator');
      this.visible = this.droplet.visible;
      this.bouncePlugin();
      await this._cognitoCheckSession();
      if (this.droplet.firstUpload) {
        const images = this.droplet.images;
        _.forEach(images, async (image, i) => {
          if (!this._isBoilerplateAsset(image.src)) {
            let file = path.basename(image.src);
            file = `${this._sitePath}/${this.bucketPath}${file}`;
            try {
              await this._s3checkFile({ path: file });
            } catch (e) {
              this.$log.warn(e);
              // this is for the event in which a user deletes
              // an image from s3 then tries to cancel. Since we
              // aren't restoring the image in s3, we need to
              // remove it from the images array after the cancel
              // action reverts the other droplet.
              const payload = {};
              payload.drp = this.droplet;
              payload.imgIndex = i;
              // remove image then bounce plugin
              await this.$store.dispatch('removeCarouselImg', payload);
              // give the jquery plugin half a second to bounce
              setTimeout(() => {
                this.bouncePlugin();
              }, 500);
            }
          }
        });
      }
    },
    setLocals(v) {
      this.$emit('set-display', v);
    },
    toggleModal() {
      this.showImgModal = !this.showImgModal;
      const content = this.$refs.confirmModal;
      if (this.showImgModal) {
        this.modelMoveOut(content);
      } else {
        this.modalMoveIn(content);
      }
    },
    formatErrors(ers) {
      return ers.join(' ');
    },
    promise(action, error) {
      const promise = new Promise((resolve, reject) => {
        if (action) {
          resolve();
        } else {
          reject(Error(`Error in carousel: ${error}`));
        }
      });
      promise.then(() => {
        this.bouncePlugin();
      });
    },
  },
});



// WEBPACK FOOTER //
// ./src/global-view/editor/droplets/carousel/components/_edit-options/index.js